(function () {
  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        revealElement(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -20px 0px'
  });

  // Counter observer — separate so counters trigger independently
  var counterObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        animateCounter(entry.target);
        counterObserver.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -20px 0px'
  });

  /**
   * Drop the compositing layer once the reveal is done.
   *
   * `will-change` on [data-reveal] is a hint for an animation that runs exactly
   * once. Left in place it pins a GPU layer for every revealed element for the
   * lifetime of the page, which on a long page costs real memory and slows
   * compositing — the opposite of what the hint is for.
   */
  function releaseWillChange(el) {
    var done = function () {
      el.style.willChange = 'auto';
      el.removeEventListener('transitionend', done);
      el.removeEventListener('animationend', done);
      clearTimeout(timer);
    };
    // Fallback for reveal types that finish without firing either event
    // (typewriter animates its children, not the element itself).
    var timer = setTimeout(done, 3000);
    el.addEventListener('transitionend', done);
    el.addEventListener('animationend', done);
  }

  function revealElement(el) {
    // Typewriter staggering is handled entirely in CSS now: each character
    // carries a --tw-i index and clean-reveal.css turns that into a
    // transition-delay. Adding .is-revealed is all it takes to start the run.
    // The old version queued one setTimeout per character, each doing its own
    // style write off the main animation clock.
    el.classList.add('is-revealed');
    releaseWillChange(el);
  }

  /**
   * Splits text content of typewriter elements into individual <span> chars.
   * Preserves child elements (like nested spans) and only splits text nodes.
   */
  function prepareTypewriters() {
    document.querySelectorAll('[data-reveal="typewriter"]:not([data-tw-ready])').forEach(function (el) {
      el.setAttribute('data-tw-ready', '');
      var fragment = document.createDocumentFragment();

      // Running index across the whole element, nested spans included. The CSS
      // turns it into transition-delay, replacing the one-timer-per-character
      // approach this used to take.
      var charIndex = 0;

      function makeChar(char) {
        var span = document.createElement('span');
        span.className = 'tw-char';
        span.textContent = char === ' ' ? ' ' : char;
        if (char === ' ') span.style.display = 'inline-block';
        span.style.setProperty('--tw-i', charIndex);
        charIndex++;
        return span;
      }

      el.childNodes.forEach(function (node) {
        if (node.nodeType === Node.TEXT_NODE) {
          // Split text into individual characters
          node.textContent.split('').forEach(function (char) {
            fragment.appendChild(makeChar(char));
          });
        } else {
          // Keep existing elements (spans, etc.) — wrap their text too
          var clone = node.cloneNode(false);
          if (node.textContent) {
            node.textContent.split('').forEach(function (char) {
              clone.appendChild(makeChar(char));
            });
          }
          fragment.appendChild(clone);
        }
      });

      el.textContent = '';
      el.appendChild(fragment);
    });
  }

  /**
   * Animates a [data-counter] element from 0 to its target value.
   */
  function animateCounter(el) {
    // Marked here rather than at the call site so both entry points — already
    // in viewport on load, and reached later via counterObserver — are covered.
    // Without this, a re-init would restart counters that had already run.
    if (el.hasAttribute('data-counter-done')) return;
    el.setAttribute('data-counter-done', '');

    var target = parseInt(el.getAttribute('data-counter'), 10);
    if (isNaN(target) || target <= 0) return;

    var duration = 1200; // ms
    var startTime = null;

    // Easing: ease-out quad
    function easeOut(t) {
      return t * (2 - t);
    }

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var progress = Math.min((timestamp - startTime) / duration, 1);
      var current = Math.round(easeOut(progress) * target);
      el.textContent = current;
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = target;
      }
    }

    requestAnimationFrame(step);
  }

  /**
   * Assign --reveal-index once per stagger container.
   *
   * This used to run from inside the per-element loop: every child looked up
   * all of its siblings and rewrote the index on the whole group, so a group of
   * n children cost n x n writes. Walking the containers instead makes it
   * linear, and keeps every write in one batch away from the reads below.
   */
  function assignStaggerIndexes() {
    document.querySelectorAll('[data-reveal-stagger]').forEach(function (parent) {
      parent.querySelectorAll(':scope > [data-reveal]').forEach(function (child, i) {
        child.style.setProperty('--reveal-index', i);
      });
    });
  }

  function init() {
    // Prepare typewriter elements before observing
    prepareTypewriters();

    // --- Write phase ---------------------------------------------------
    assignStaggerIndexes();

    /*
     * --- Read phase ----------------------------------------------------
     * All geometry is collected before anything is written back. Previously
     * the loop alternated setProperty() with getBoundingClientRect(), which
     * forces the browser to re-run layout on every single iteration.
     */
    var revealTargets = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
    var viewportH = window.innerHeight;
    var viewportW = window.innerWidth;

    var revealRects = [];
    revealTargets.forEach(function (el) {
      revealRects.push(el.getBoundingClientRect());
    });

    var counterTargets = document.querySelectorAll('[data-counter]:not([data-counter-done])');
    var counterRects = [];
    counterTargets.forEach(function (el) {
      counterRects.push(el.getBoundingClientRect());
    });

    // --- Act phase -----------------------------------------------------
    revealTargets.forEach(function (el, i) {
      var rect = revealRects[i];
      var inView =
        rect.top < viewportH + 60 &&
        rect.bottom > -60 &&
        rect.left < viewportW &&
        rect.right > 0;

      if (inView) {
        requestAnimationFrame(function () {
          revealElement(el);
        });
      } else {
        observer.observe(el);
      }
    });

    counterTargets.forEach(function (el, i) {
      var rect = counterRects[i];
      if (rect.top < viewportH - 20 && rect.bottom > 0) {
        animateCounter(el);
      } else {
        counterObserver.observe(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init for dynamically added content
  document.addEventListener('nude:grid:updated', init);
})();
